import React, { useEffect, useRef, useState } from "react";
import { FormData, SetFormData } from "./FormData.tsx";
import { FaClock, FaInfoCircle } from "react-icons/fa";
import { MdFreeCancellation } from "react-icons/md";
import "../../../App.css";
import { BookingType } from "../../../assets/BookingType.tsx";

interface InfoPopupProps {
  content: React.ReactNode;
  isVisible: boolean;
  position: string;
}

const InfoPopup = ({ content, isVisible, position }: InfoPopupProps) => {
  return (
    <div
      className={`absolute z-10 p-3 bg-white border border-gray-300 rounded-lg shadow-lg text-sm max-w-xs transition-opacity duration-200 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      } ${position}`}
      style={{ minWidth: "200px" }}
    >
      {content}
      <div className="absolute w-3 h-3 bg-white transform rotate-45 -left-1.5 top-4 border-l border-t border-gray-300"></div>
    </div>
  );
};

const BookingTypeInfo = () => (
  <div>
    <p className="font-semibold mb-1">Booking Time Options:</p>
    <ul className="list-disc pl-5 space-y-1">
      <li>
        <strong>Hours:</strong> Bookable by the hour
      </li>
      <li>
        <strong>Days:</strong> Bookable by the day
      </li>
      <li>
        <strong>Weeks:</strong> Bookable by the week
      </li>
      <li>
        <strong>Months:</strong> Bookable by the month
      </li>
      <li>
        <strong>Years:</strong> Bookable by the year
      </li>
    </ul>
  </div>
);

const CancellationPolicyInfo = () => (
  <div>
    <p className="font-semibold mb-1">Cancellation Policies:</p>
    <ul className="list-disc pl-5 space-y-1">
      <li>
        <strong>Flexible:</strong> Full refund up to 24 hours before booking
      </li>
      <li>
        <strong>Moderate:</strong> Full refund up to 5 days before booking
      </li>
      <li>
        <strong>Strict:</strong> 50% refund up to 7 days before booking
      </li>
    </ul>
  </div>
);

// const InstantBookableInfo = () => (
//   <div>
//     <p className="font-semibold mb-1">Instant Booking Options:</p>
//     <ul className="list-disc pl-5 space-y-1">
//       <li>
//         <strong>Yes:</strong> Users can directly book the workspace with immediate payment. The booking is confirmed instantly.
//       </li>
//       <li>
//         <strong>No:</strong> Users can request to book the workspace, but payment will not be processed until you confirm their booking request.
//       </li>
//     </ul>
//   </div>
// );

interface listingForm {
  formData: FormData;
  setFormData: SetFormData;
  setNextDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

interface BookingTypeDropdownProps {
  value: BookingType;
  onChange: (value: BookingType) => void;
}

interface CancellationPolicyDropdownProps {
  value: CancellationPolicy;
  onChange: (value: CancellationPolicy) => void;
}

enum CancellationPolicy {
  Flexible = 0,
  Moderate = 1,
  Strict = 2,
}

function CancellationPolicyDropdown({
  value,
  onChange,
}: CancellationPolicyDropdownProps) {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(parseInt(e.target.value) as CancellationPolicy);
  };

  return (
    <select
      name="minimumBookingType"
      value={value}
      onChange={handleChange}
      className="w-full p-2 border rounded"
    >
      <option value={CancellationPolicy.Flexible}>Flexible</option>
      <option value={CancellationPolicy.Moderate}>Moderate</option>
      <option value={CancellationPolicy.Strict}>Strict</option>
    </select>
  );
}

function BookingTypeDropdown({ value, onChange }: BookingTypeDropdownProps) {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(parseInt(e.target.value) as BookingType);
  };

  return (
    <select
      name="minimumBookingType"
      value={value}
      onChange={handleChange}
      className="w-full p-2 border rounded"
    >
      <option value={BookingType.IsHours}>Hours</option>
      <option value={BookingType.IsDays}>Days</option>
      <option value={BookingType.IsWeeks}>Weeks</option>
      <option value={BookingType.IsMonths}>Months</option>
      <option value={BookingType.IsYears}>Years</option>
    </select>
  );
}

export default function WorkspaceBookingDetails({
  formData,
  setFormData,
  setNextDisabled,
}: listingForm) {
  const [showBookingInfo, setShowBookingInfo] = useState(false);
  const [showCancellationInfo, setShowCancellationInfo] = useState(false);
  // const [showInstantBookableInfo, setShowInstantBookableInfo] = useState(false);

  const formatPrice = (value: number | null) =>
    value !== null && value > 0 ? `$${value.toFixed(2)}` : "";

  const [localPrices, setLocalPrices] = useState({
    pricePerYear: formatPrice(formData.pricePerYear),
    pricePerMonth: formatPrice(formData.pricePerMonth),
    pricePerWeek: formatPrice(formData.pricePerWeek),
    pricePerDay: formatPrice(formData.pricePerDay),
    pricePerHour: formatPrice(formData.pricePerHour),
  });

  const focusedFieldRef = useRef<string | null>(null);

  // Sync local prices when formData changes externally (e.g. autoFillPrices)
  useEffect(() => {
    setLocalPrices((prev) => ({
      pricePerYear: focusedFieldRef.current !== "pricePerYear" ? formatPrice(formData.pricePerYear) : prev.pricePerYear,
      pricePerMonth: focusedFieldRef.current !== "pricePerMonth" ? formatPrice(formData.pricePerMonth) : prev.pricePerMonth,
      pricePerWeek: focusedFieldRef.current !== "pricePerWeek" ? formatPrice(formData.pricePerWeek) : prev.pricePerWeek,
      pricePerDay: focusedFieldRef.current !== "pricePerDay" ? formatPrice(formData.pricePerDay) : prev.pricePerDay,
      pricePerHour: focusedFieldRef.current !== "pricePerHour" ? formatPrice(formData.pricePerHour) : prev.pricePerHour,
    }));
  }, [formData.pricePerYear, formData.pricePerMonth, formData.pricePerWeek, formData.pricePerDay, formData.pricePerHour]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | number | boolean | null,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const autoFillPrices = () => {
    // If pricePerYear is null or not provided, do nothing
    if (formData.pricePerYear === null || formData.pricePerYear === undefined)
      return;

    const pricePerYear = formData.pricePerYear;

    // Calculate all other prices based on pricePerYear and round to 2 decimals
    const roundTo2 = (number: number) => Math.round(number * 100) / 100;

    const pricePerMonth = roundTo2(pricePerYear / 12);
    const pricePerWeek = roundTo2(pricePerYear / 52);
    const pricePerDay = roundTo2(pricePerYear / 365);
    const pricePerHour = roundTo2(pricePerYear / (365 * 24));

    // Update the form data with calculated prices
    setFormData((prev) => ({
      ...prev,
      pricePerMonth,
      pricePerWeek,
      pricePerDay,
      pricePerHour,
      // pricePerYear remains unchanged
    }));
  };
  useEffect(() => {
    const requiredFieldsValid =
      formData.minimumBookingTime > 0 &&
      formData.pricePerYear > 0 &&
      formData.pricePerHour > 0 &&
      formData.pricePerDay > 0 &&
      formData.pricePerWeek > 0 &&
      formData.pricePerMonth > 0;

    const pricingValid =
      formData.pricePerHour > 0 ||
      formData.pricePerDay > 0 ||
      formData.pricePerWeek > 0 ||
      formData.pricePerMonth > 0 ||
      formData.pricePerYear > 0;

    setNextDisabled(!(requiredFieldsValid && pricingValid));
  }, [formData]);

  return (
    <div className="space-y-4">
      <h2 className="text-3xl font-bold mb-4">
        What are your booking details and type?
      </h2>
      <hr className="border-t border-gray-500 my-4 w-full shadow-gray-400" />
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
        {/* Booking Type Selection */}
        <div className="p-4 border border-gray-200 rounded-lg w-full flex-row flex gap-15">
          <div>
            <div className="flex flex-row items-center gap-3 mb-3">
              <FaClock className="h-6 w-6 text-gray-500" />
              <h3 className="text-lg font-medium">Minimum Booking Time</h3>
              <div className="relative flex items-center">
                <button
                  onMouseEnter={() => setShowBookingInfo(true)}
                  onMouseLeave={() => setShowBookingInfo(false)}
                  onClick={() => setShowBookingInfo(!showBookingInfo)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none flex items-center"
                >
                  <FaInfoCircle className="h-5 w-5" />
                </button>
                <InfoPopup
                  content={<BookingTypeInfo />}
                  isVisible={showBookingInfo}
                  position="left-0 top-8"
                />
              </div>
            </div>
            <div className="mb-2">
              <h4 className="text-sm font-light">
                Minimum time required to book this workspace
              </h4>
            </div>
            <div className="space-y-2 flex flex-col sm:flex-row sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking time type
                </label>
                <BookingTypeDropdown
                  value={formData.minimumBookingType}
                  onChange={(e) =>
                    handleInputChange("minimumBookingType", Number(e))
                  }
                />
              </div>
              <div>
                <br />
                <input
                  type="number"
                  value={formData.minimumBookingTime || 0}
                  onChange={(e) =>
                    handleInputChange(
                      "minimumBookingTime",
                      Number(e.target.value),
                    )
                  }
                  className="w-15 p-2 border rounded"
                  min="0"
                  placeholder="Required booking time"
                />
              </div>
            </div>
          </div>
          <div>
            <div>
              <div className="relative">
                <div className="flex flex-row items-center gap-3 mb-3">
                  <MdFreeCancellation className="h-6 w-6 text-gray-500" />
                  <h3 className="text-lg font-medium">Cancellation Policy</h3>
                  <div className="relative flex items-center">
                    <button
                      onMouseEnter={() => setShowCancellationInfo(true)}
                      onMouseLeave={() => setShowCancellationInfo(false)}
                      onClick={() =>
                        setShowCancellationInfo(!showCancellationInfo)
                      }
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <FaInfoCircle className="h-5 w-5" />
                    </button>
                    <InfoPopup
                      content={<CancellationPolicyInfo />}
                      isVisible={showCancellationInfo}
                      position="left-0 top-8"
                    />
                  </div>
                </div>
              </div>
              <div className="mb-2">
                <br />
              </div>
              <div className="space-y-2 flex flex-col sm:flex-row sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cancellation Policy Type
                  </label>
                  <CancellationPolicyDropdown
                    value={formData.cancellationPolicy}
                    onChange={(e) =>
                      handleInputChange("cancellationPolicy", Number(e))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Instant Bookable Section */}
        <div className="p-4 border border-gray-200 rounded-lg w-full">
          {/*<div className="flex flex-row items-center gap-3 mb-3">*/}
          {/*  <FaClock className="h-6 w-6 text-gray-500" />*/}
          {/*  <h3 className="text-lg font-medium">Instant Booking</h3>*/}
          {/*  <div className="relative flex items-center">*/}
          {/*    <button*/}
          {/*      onMouseEnter={() => setShowInstantBookableInfo(true)}*/}
          {/*      onMouseLeave={() => setShowInstantBookableInfo(false)}*/}
          {/*      onClick={() => setShowInstantBookableInfo(!showInstantBookableInfo)}*/}
          {/*      className="text-gray-400 hover:text-gray-600 focus:outline-none flex items-center"*/}
          {/*    >*/}
          {/*      <FaInfoCircle className="h-5 w-5" />*/}
          {/*    </button>*/}
          {/*    <InfoPopup*/}
          {/*      content={<InstantBookableInfo />}*/}
          {/*      isVisible={showInstantBookableInfo}*/}
          {/*      position="left-0 top-8"*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*</div>*/}
          <div className="mb-2">
            <h4 className="text-sm font-light">
              Allow users to book your workspace instantly with payment, or
              require your confirmation first
            </h4>
          </div>
          <div className="flex gap-6 mt-4">
            <label className="flex items-center cursor-pointer">
              <span className="mr-3 text-sm font-medium text-gray-700">
                Yes
              </span>
              <input
                type="radio"
                name="instantBookable"
                checked={formData.isInstantBookable === true}
                onChange={() => handleInputChange("isInstantBookable", true)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
            </label>
            <label className="flex items-center cursor-pointer">
              <span className="mr-3 text-sm font-medium text-gray-700">No</span>
              <input
                type="radio"
                name="instantBookable"
                checked={formData.isInstantBookable === false}
                onChange={() => handleInputChange("isInstantBookable", false)}
                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              />
            </label>
          </div>
        </div>

              {/* Pricing Inputs */}
              <div className="p-4 border border-gray-200 rounded-lg w-full">
                  <div className="flex justify-between items-center gap-3 mb-3">
                      <h3 className="text-lg font-medium mb-3">Pricing</h3>
                      <button
                          className="green-button mb-3"
                          disabled={formData.pricePerYear === null}
                          onClick={() => autoFillPrices()}
                      >
                          Auto Fill Prices
                      </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Price Per Year */}
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price Per Year ($)
                          </label>
                          <input
                              type="text"
                              value={localPrices.pricePerYear}
                              onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.]/g, "");
                                  setLocalPrices((prev) => ({ ...prev, pricePerYear: raw }));
                              }}
                              onFocus={() => {
                                  focusedFieldRef.current = "pricePerYear";
                                  setLocalPrices((prev) => ({
                                      ...prev,
                                      pricePerYear: formData.pricePerYear > 0 ? formData.pricePerYear.toString() : "",
                                  }));
                              }}
                              onBlur={() => {
                                  focusedFieldRef.current = null;
                                  const raw = localPrices.pricePerYear.replace(/[^0-9.]/g, "");
                                  const parsed = raw ? parseFloat(raw) : null;
                                  handleInputChange("pricePerYear", parsed);
                                  setLocalPrices((prev) => ({ ...prev, pricePerYear: formatPrice(parsed) }));
                              }}
                              className="w-full p-2 border rounded placeholder-gray-400"
                              placeholder="$0.00"
                          />
                      </div>

                      {/* Price Per Month */}
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price Per Month ($)
                          </label>
                          <input
                              type="text"
                              value={localPrices.pricePerMonth}
                              onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.]/g, "");
                                  setLocalPrices((prev) => ({ ...prev, pricePerMonth: raw }));
                              }}
                              onFocus={() => {
                                  focusedFieldRef.current = "pricePerMonth";
                                  setLocalPrices((prev) => ({
                                      ...prev,
                                      pricePerMonth: formData.pricePerMonth > 0 ? formData.pricePerMonth.toString() : "",
                                  }));
                              }}
                              onBlur={() => {
                                  focusedFieldRef.current = null;
                                  const raw = localPrices.pricePerMonth.replace(/[^0-9.]/g, "");
                                  const parsed = raw ? parseFloat(raw) : null;
                                  handleInputChange("pricePerMonth", parsed);
                                  setLocalPrices((prev) => ({ ...prev, pricePerMonth: formatPrice(parsed) }));
                              }}
                              className="w-full p-2 border rounded placeholder-gray-400"
                              placeholder="$0.00"
                          />
                      </div>

                      {/* Price Per Week */}
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price Per Week ($)
                          </label>
                          <input
                              type="text"
                              value={localPrices.pricePerWeek}
                              onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.]/g, "");
                                  setLocalPrices((prev) => ({ ...prev, pricePerWeek: raw }));
                              }}
                              onFocus={() => {
                                  focusedFieldRef.current = "pricePerWeek";
                                  setLocalPrices((prev) => ({
                                      ...prev,
                                      pricePerWeek: formData.pricePerWeek > 0 ? formData.pricePerWeek.toString() : "",
                                  }));
                              }}
                              onBlur={() => {
                                  focusedFieldRef.current = null;
                                  const raw = localPrices.pricePerWeek.replace(/[^0-9.]/g, "");
                                  const parsed = raw ? parseFloat(raw) : null;
                                  handleInputChange("pricePerWeek", parsed);
                                  setLocalPrices((prev) => ({ ...prev, pricePerWeek: formatPrice(parsed) }));
                              }}
                              className="w-full p-2 border rounded placeholder-gray-400"
                              placeholder="$0.00"
                          />
                      </div>

                      {/* Price Per Day */}
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price Per Day ($)
                          </label>
                          <input
                              type="text"
                              value={localPrices.pricePerDay}
                              onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.]/g, "");
                                  setLocalPrices((prev) => ({ ...prev, pricePerDay: raw }));
                              }}
                              onFocus={() => {
                                  focusedFieldRef.current = "pricePerDay";
                                  setLocalPrices((prev) => ({
                                      ...prev,
                                      pricePerDay: formData.pricePerDay > 0 ? formData.pricePerDay.toString() : "",
                                  }));
                              }}
                              onBlur={() => {
                                  focusedFieldRef.current = null;
                                  const raw = localPrices.pricePerDay.replace(/[^0-9.]/g, "");
                                  const parsed = raw ? parseFloat(raw) : null;
                                  handleInputChange("pricePerDay", parsed);
                                  setLocalPrices((prev) => ({ ...prev, pricePerDay: formatPrice(parsed) }));
                              }}
                              className="w-full p-2 border rounded placeholder-gray-400"
                              placeholder="$0.00"
                          />
                      </div>

                      {/* Price Per Hour */}
                      <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                              Price Per Hour ($)
                          </label>
                          <input
                              type="text"
                              value={localPrices.pricePerHour}
                              onChange={(e) => {
                                  const raw = e.target.value.replace(/[^0-9.]/g, "");
                                  setLocalPrices((prev) => ({ ...prev, pricePerHour: raw }));
                              }}
                              onFocus={() => {
                                  focusedFieldRef.current = "pricePerHour";
                                  setLocalPrices((prev) => ({
                                      ...prev,
                                      pricePerHour: formData.pricePerHour > 0 ? formData.pricePerHour.toString() : "",
                                  }));
                              }}
                              onBlur={() => {
                                  focusedFieldRef.current = null;
                                  const raw = localPrices.pricePerHour.replace(/[^0-9.]/g, "");
                                  const parsed = raw ? parseFloat(raw) : null;
                                  handleInputChange("pricePerHour", parsed);
                                  setLocalPrices((prev) => ({ ...prev, pricePerHour: formatPrice(parsed) }));
                              }}
                              className="w-full p-2 border rounded placeholder-gray-400"
                              placeholder="$0.00"
                          />
                      </div>
                  </div>
              </div>
      </div>
    </div>
  );
}
